import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, LogOut, MessageSquare, RefreshCw, Trash2 } from "lucide-react";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKDAYS = ["domenica", "lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato"];
const MONTHS = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];

const dayLabel = (dateStr) => {
  const d = new Date(`${dateStr}T12:00:00`);
  const wd = WEEKDAYS[d.getDay()];
  return `${wd.charAt(0).toUpperCase()}${wd.slice(1)} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
};

const formatApiError = (detail) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return "Errore imprevisto";
};

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-gold focus:ring-2 focus:ring-gold/30";

const Admin = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem("admin_token"));
  const [login, setLogin] = useState({ email: "", password: "" });
  const [loginLoading, setLoginLoading] = useState(false);
  const [tab, setTab] = useState("appointments");
  const [appointments, setAppointments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoadingData(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [a, c] = await Promise.all([
        axios.get(`${API}/admin/appointments`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers }),
      ]);
      setAppointments(a.data.appointments);
      setContacts(c.data.contacts);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem("admin_token");
        setToken(null);
        toast.error("Sessione scaduta. Effettua di nuovo l'accesso.");
      } else {
        toast.error("Impossibile caricare i dati.");
      }
    } finally {
      setLoadingData(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const r = await axios.post(`${API}/admin/login`, login);
      sessionStorage.setItem("admin_token", r.data.token);
      setToken(r.data.token);
      toast.success("Accesso effettuato");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Accesso non riuscito");
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("admin_token");
    setToken(null);
  };

  const downloadCsv = async (what) => {
    try {
      const r = await axios.get(`${API}/admin/export/${what}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = what === "appointments" ? "appuntamenti.csv" : "richieste-contatto.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download non riuscito");
    }
  };

  const deleteAppointment = async (appt) => {
    if (!window.confirm(`Eliminare la richiesta di ${appt.name} per ${dayLabel(appt.date)} ore ${appt.time}? Lo slot tornerà disponibile.`)) return;
    try {
      await axios.delete(`${API}/admin/appointments/${appt.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Prenotazione eliminata: lo slot è di nuovo disponibile.");
      loadData();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Eliminazione non riuscita");
    }
  };

  if (!token) {
    return (
      <section className="bg-gold-soft/60 py-20" data-testid="admin-login-page">
        <Seo title="Area riservata | Studio Legale Avv. Manfredi Bruno" description="Area riservata allo studio." path="/admin" />
        <div className="container-site max-w-md">
          <form onSubmit={doLogin} data-testid="admin-login-form" className="rounded-2xl border border-slate-200 bg-white p-8 shadow-lg">
            <h1 className="font-serif text-2xl font-semibold text-navy">Area riservata</h1>
            <p className="mt-2 text-sm text-slate-600">Accesso riservato allo studio.</p>
            <div className="mt-6 space-y-4">
              <input
                type="email"
                required
                value={login.email}
                onChange={(e) => setLogin((l) => ({ ...l, email: e.target.value }))}
                className={inputCls}
                placeholder="Email"
                aria-label="Email amministratore"
                data-testid="admin-email-input"
                autoComplete="username"
              />
              <input
                type="password"
                required
                value={login.password}
                onChange={(e) => setLogin((l) => ({ ...l, password: e.target.value }))}
                className={inputCls}
                placeholder="Password"
                aria-label="Password amministratore"
                data-testid="admin-password-input"
                autoComplete="current-password"
              />
              <button
                type="submit"
                disabled={loginLoading}
                data-testid="admin-login-submit"
                className="btn-gold w-full disabled:opacity-60"
              >
                {loginLoading ? "Accesso in corso..." : "Accedi"}
              </button>
            </div>
          </form>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-gold-soft/60 py-14" data-testid="admin-dashboard">
      <Seo title="Area riservata | Studio Legale Avv. Manfredi Bruno" description="Area riservata allo studio." path="/admin" />
      <div className="container-site">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-serif text-3xl font-semibold text-navy">Area riservata</h1>
          <div className="flex gap-2">
            <button type="button" onClick={loadData} data-testid="admin-refresh-button" className="btn-outline-navy !px-4 !py-2 text-xs">
              <RefreshCw size={14} aria-hidden="true" /> Aggiorna
            </button>
            <button type="button" onClick={logout} data-testid="admin-logout-button" className="btn-outline-navy !px-4 !py-2 text-xs">
              <LogOut size={14} aria-hidden="true" /> Esci
            </button>
          </div>
        </div>

        <div className="mt-6 flex gap-2">
          <button
            type="button"
            onClick={() => setTab("appointments")}
            data-testid="admin-tab-appointments"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
              tab === "appointments" ? "bg-navy text-white" : "border border-slate-300 bg-white text-navy hover:border-gold"
            }`}
          >
            <CalendarDays size={15} aria-hidden="true" /> Appuntamenti ({appointments.length})
          </button>
          <button
            type="button"
            onClick={() => setTab("contacts")}
            data-testid="admin-tab-contacts"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
              tab === "contacts" ? "bg-navy text-white" : "border border-slate-300 bg-white text-navy hover:border-gold"
            }`}
          >
            <MessageSquare size={15} aria-hidden="true" /> Richieste di contatto ({contacts.length})
          </button>
          <button
            type="button"
            onClick={() => downloadCsv("appointments")}
            data-testid="admin-export-appointments"
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2.5 text-xs font-semibold text-navy transition-colors hover:border-gold"
          >
            Appuntamenti CSV
          </button>
          <button
            type="button"
            onClick={() => downloadCsv("contacts")}
            data-testid="admin-export-contacts"
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2.5 text-xs font-semibold text-navy transition-colors hover:border-gold"
          >
            Contatti CSV
          </button>
        </div>

        {loadingData && <p className="mt-8 text-sm text-slate-500">Caricamento...</p>}

        {!loadingData && tab === "appointments" && (
          <div className="mt-6 space-y-4" data-testid="admin-appointments-list">
            {appointments.length === 0 && (
              <p className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600">
                Nessuna richiesta di appuntamento presente.
              </p>
            )}
            {appointments.map((a) => (
              <div key={a.id} data-testid={`admin-appointment-${a.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-serif text-lg font-semibold text-navy">
                      {dayLabel(a.date)} · ore {a.time}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-slate-800">{a.name}</p>
                    {(a.topic || a.sex || a.age_range) && (
                      <p className="mt-1 text-xs text-slate-500">
                        {[a.topic, a.sex, a.age_range].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    <p className="mt-1 text-sm text-slate-600">
                      <a href={`tel:${a.phone}`} className="font-medium text-gold-dark">{a.phone}</a>
                      {" · "}
                      <a href={`mailto:${a.email}`} className="text-gold-dark">{a.email}</a>
                    </p>
                    {a.note && <p className="mt-2 text-sm leading-relaxed text-slate-600">Motivo: {a.note}</p>}
                    <p className="mt-2 text-xs text-slate-400">
                      Ricevuta il {a.created_at?.slice(0, 16).replace("T", " ")}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => deleteAppointment(a)}
                    data-testid={`admin-delete-${a.id}`}
                    className="inline-flex items-center gap-2 rounded-full border border-red-200 px-4 py-2 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50"
                  >
                    <Trash2 size={14} aria-hidden="true" /> Elimina e reintegra slot
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loadingData && tab === "contacts" && (
          <div className="mt-6 space-y-4" data-testid="admin-contacts-list">
            {contacts.length === 0 && (
              <p className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600">
                Nessuna richiesta di contatto presente.
              </p>
            )}
            {contacts.map((c) => (
              <div key={c.id} data-testid={`admin-contact-${c.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-navy">{c.name}</p>
                  <span className="rounded-full bg-gold-soft px-3 py-1 text-xs font-semibold text-gold-dark">{c.topic}</span>
                </div>
                <p className="mt-1 text-sm text-slate-600">
                  <a href={`mailto:${c.email}`} className="text-gold-dark">{c.email}</a>
                  {c.phone && <> · <a href={`tel:${c.phone}`} className="text-gold-dark">{c.phone}</a></>}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">{c.message}</p>
                <p className="mt-2 text-xs text-slate-400">Ricevuta il {c.created_at?.slice(0, 16).replace("T", " ")}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Admin;
